<template>
  <div class="catalog">
    <NavBar v-if="renderAsView">
      <template #left>
        <BackButton @click="handleBack" />
      </template>
    </NavBar>
    <LoaderIndicator v-if="isLoading" class="catalog__loader" with-container />
    <HelperViewStatus
      v-else-if="hasError"
      class="catalog__helper"
      generic-error
      button-label="Tentar novamente"
      @click="fetchCatalog"
    />
    <main v-else-if="catalog" :class="{ catalog__content: renderAsView }">
      <section v-if="objectivePortfolio" class="catalog__section">
        <Heading v-if="renderAsView" :level="2">
          {{ catalog.recommended.title }}
        </Heading>
        <ObjectiveCard
          class="catalog__objective-card"
          :title="objectivePortfolio.description"
          :head="objectivePortfolio.title"
          :image="objectivePortfolio.image.full"
          @click="
            goToFundsBuilder(
              objectivePortfolio.type,
              objectivePortfolio.askForInvestorType
            )
          "
        />
      </section>
      <section
        v-if="catalog.funds.portfolios.length > 0"
        class="catalog__section"
      >
        <HeadingDisplay
          size="small"
          :title="catalog.funds.title"
          :subtitle="catalog.funds.subtitle"
        />
        <Grid :columns="[1, 1]" gutter="xm">
          <WalletCard
            v-for="(portfolio, index) in catalog.funds.portfolios"
            :key="index"
            data-testid="wallet-card-funds"
            class="catalog__wallet-card"
            :title="portfolio.title"
            :subtitle="portfolio.description"
            :image="portfolio.image.full"
            @click="
              goToFundsBuilder(portfolio.type, portfolio.askForInvestorType)
            "
          />
        </Grid>
      </section>
      <section
        v-if="catalog.variableIncome.portfolios.length > 0"
        class="catalog__section"
      >
        <HeadingDisplay
          size="small"
          :title="catalog.variableIncome.title"
          :subtitle="catalog.variableIncome.subtitle"
        />
        <Grid :columns="[1, 1]" gutter="xm">
          <WalletCard
            v-for="(portfolio, index) in catalog.variableIncome.portfolios"
            :key="index"
            data-testid="wallet-card-variable-income"
            class="catalog__wallet-card"
            :title="portfolio.title"
            :subtitle="portfolio.description"
            :image="portfolio.image.full"
            @click="goToVariableIncomeBuilder(portfolio.id)"
          />
        </Grid>
      </section>
      <section v-if="catalog.investByProduct" class="catalog__section">
        <HeadingDisplay
          size="small"
          :title="catalog.investByProduct.title"
          :subtitle="catalog.investByProduct.subtitle"
        />
        <Grid :columns="[1, 1, 1]" gutter="xm">
          <ButtonBoxDecide
            v-for="(item, index) in catalog.investByProduct.buttons"
            :key="index"
            class="card"
            :icon="item.icon"
            :label="item.label"
            :badge-label="item.chip"
            @click="goToCatalog(item.productType)"
          />
        </Grid>
      </section>
    </main>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeMount, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';

import {
  NavBar,
  LoaderIndicator,
  HelperViewStatus,
  Heading,
  HeadingDisplay,
  Grid,
  ButtonBoxDecide
} from '@warrenbrasil/nebraska-web';
import { BackButton } from '@/modules/common/components/buttons';
import { ObjectiveCard } from './components/ObjectiveCard';
import { WalletCard } from './components/WalletCard';
import { getCatalog, ICatalog } from '@/modules/catalog/services/catalog';
import { useCatalogStore } from '@/modules/catalog/store';

const router = useRouter();
const route = useRoute();

const props = withDefaults(
  defineProps<{
    renderAsView?: boolean;
    routeInvestmentGuard?: () => void;
  }>(),
  {
    renderAsView: true,
    routeInvestmentGuard: () => {}
  }
);

const catalog = ref<ICatalog | null>(null);
const isLoading = ref(true);
const hasError = ref(false);

const objectivePortfolio = computed(() => {
  return catalog.value?.recommended.portfolios[0];
});

function handleBack() {
  if (useCatalogStore().fromUserFlow) {
    router.push({
      name: useCatalogStore().fromUserFlow,
      query: { cameFrom: useCatalogStore().fromUserCameFrom }
    });
  } else {
    const cameFrom = (route.query?.module as string) || 'wealth';
    router.push({ name: cameFrom });
  }
}

function goToFundsBuilder(type: string, askForInvestorType: boolean) {
  props.routeInvestmentGuard();

  const query = askForInvestorType
    ? { qualifiedInvestor: String(askForInvestorType) }
    : {};

  router.push({
    name: 'fundsAcquisition',
    params: { type },
    query
  });
}

function goToCatalog(tab: string) {
  props.routeInvestmentGuard();

  router.push({
    name: 'catalogBuilder',
    params: {
      type: tab || 'fixed-income',
      cameFrom: 'catalog'
    }
  });
}

function goToVariableIncomeBuilder(id: string) {
  router.push({
    name: 'variableIncomeAcquisition',
    params: { id }
  });
}

async function fetchCatalog() {
  try {
    isLoading.value = true;
    hasError.value = false;

    catalog.value = await getCatalog();
  } catch (error) {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

onBeforeMount(async () => {
  await fetchCatalog();
});
</script>

<style lang="less" scoped>
@height: calc(100vh - 128px);

.catalog {
  .navbar-container {
    border-bottom: none;
  }

  &__loader {
    height: @height;
  }

  &__helper {
    margin: 0 auto;
    height: @height;
    justify-content: center;
  }

  &__content {
    max-width: 832px;
    margin: 0 auto;
    padding: 0 @size-spacing-x400;
  }

  &__funds-section {
    margin-top: @size-spacing-x1200;
  }

  &__section {
    margin-top: @size-spacing-x1200;

    &:last-of-type {
      margin-bottom: @size-spacing-x1200;
    }

    .card {
      margin-top: @size-spacing-x500;
      :deep(.secondary-text) {
        margin-top: 0;
      }

      :deep(.badge-label) {
        color: @base-primary;
        background-color: @element-primary;
        border-radius: @size-radius-max;
      }
    }
  }

  &__objective-card,
  &__wallet-card {
    margin-top: @size-spacing-x500;
  }

  &__row {
    :deep & {
      .wrapper {
        align-items: center;
      }

      .icon {
        margin-top: 0;
      }
    }
  }
}
</style>
